/**
 * Tokens visuais do dashboard.
 *
 * A tela foge de propósito do visual do resto do sistema (Paper cinza + fonte
 * padrão): aqui a densidade de informação é muito maior, e o que funciona numa
 * tela de CRUD deixa um painel ilegível. Tudo o que é cor, espaçamento e
 * tipografia mora aqui, para o restante dos arquivos falar em papéis
 * ("série 1", "tinta secundária") e não em hex solto.
 */

// --- Paleta de séries -------------------------------------------------------
//
// Dois slots, nesta ordem fixa. Validados para daltonismo contra o fundo branco
// dos cards (ΔE deutan 23,9 · tritan 15,9 · visão normal 28,6 — alvo ≥ 8 e ≥ 15;
// contraste ≥ 3:1 nos dois). O verde é a cor da marca (#256F44) clareada para
// entrar na faixa de luminosidade que a validação exige — o #256F44 puro passa,
// mas com folga bem menor no eixo tritan.
//
// Regra: a cor segue a ENTIDADE, nunca a posição no ranking. "Preenchimentos"
// é sempre verde e "Pessoas" é sempre violeta, em todos os gráficos.
export const SERIES = {
  fills: "#1f8a5b",
  people: "#4a3aa7",
};

// Cinza de desênfase: usado para apagar uma parte da MESMA série (fim de semana
// no gráfico por dia da semana). Nunca para identificar uma série diferente.
export const MUTED_MARK = "#c7ccc9";

// --- Superfícies e tinta ----------------------------------------------------
export const COLORS = {
  page: "#f4f6f5",
  surface: "#ffffff",
  // Fundo de trilhos, chips inativos e áreas de apoio.
  subtle: "#eef1ef",
  border: "rgba(14, 32, 24, 0.09)",
  // Hairline sólida (nunca tracejada: tracejado lê como projeção/limite).
  grid: "#e8ebe9",
  axis: "#d3d8d5",

  ink: "#101c16",
  inkSecondary: "#5a6560",
  inkMuted: "#8b948f",

  brand: "#256F44",
  brandDark: "#1b5334",

  // Status: só quando a cor SIGNIFICA estado. Sempre acompanhados de rótulo.
  good: "#0ca30c",
  warning: "#fab219",
  critical: "#d03b3b",
};

// Inter com fallback de sistema — se o Google Fonts não carregar, a tela cai
// num sans nativo moderno em vez de no serifado padrão do navegador.
export const FONT =
  '"Inter", system-ui, -apple-system, "Segoe UI Variable Text", "Segoe UI", Roboto, sans-serif';

export const RADIUS = 14;

// --- Helpers de formatação --------------------------------------------------

// Números grandes ficam ilegíveis inteiros num card estreito: 12.480 -> "12,5 mil".
// Valores já formatados (o "3/5" de compartilhados) passam direto.
export const compactNumber = (value) => {
  const n = Number(value);
  if (!isFinite(n)) {
    return String(value);
  }
  if (n < 1000) {
    return String(n);
  }
  if (n < 1000000) {
    return `${(n / 1000).toFixed(n < 10000 ? 1 : 0).replace(".", ",")} mil`;
  }
  return `${(n / 1000000).toFixed(1).replace(".", ",")} mi`;
};

export const decimal = (value) => String(value).replace(".", ",");

/**
 * Marcas "redondas" para o eixo de valor.
 *
 * Deixado por conta do recharts, o topo do eixo vira o maior valor do dado e as
 * marcas saem 0/65/130/195/260 — aritmeticamente corretas e horríveis de ler.
 * Aqui o passo é arredondado para 1, 2, 3, 4, 5 ou 10 vezes uma potência de dez,
 * e as marcas são calculadas a partir dele: 0/75/150/225/300.
 *
 * Só devolver o teto não basta — um teto de 150 com cinco marcas dá 37,5 por
 * marca, que o recharts arredonda para 0/40/80/150 (desigual). Por isso a
 * função devolve o `domain` E a lista de `ticks`, e os dois são passados juntos.
 *
 * Assume contagens (inteiros), que é o que todo eixo deste dashboard mostra.
 */
export const niceTicks = (values, intervals = 3) => {
  const max = values.reduce((acc, value) => Math.max(acc, Number(value) || 0), 0);

  if (max <= 0) {
    return { max: intervals, ticks: Array.from({ length: intervals + 1 }, (_, i) => i) };
  }

  const rawStep = max / intervals;
  const magnitude = Math.max(1, Math.pow(10, Math.floor(Math.log10(rawStep))));
  const candidates = [1, 2, 3, 4, 5, 10];
  const factor = candidates.find((c) => c * magnitude >= rawStep) || 10;
  const step = factor * magnitude;

  return {
    max: step * intervals,
    ticks: Array.from({ length: intervals + 1 }, (_, i) => i * step),
  };
};

export const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }
  // A API devolve o timestamp em UTC ("2026-03-03 12:00:00"); o T + Z faz o
  // navegador converter para o fuso local em vez de ler como hora local.
  const parsed = new Date(String(value).replace(" ", "T") + "Z");
  if (isNaN(parsed.getTime())) {
    return "—";
  }
  return parsed.toLocaleDateString() + " " + parsed.toLocaleTimeString().slice(0, 5);
};

// --- Estilos compartilhados de card ----------------------------------------
export const cardStyle = {
  backgroundColor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS,
  boxShadow: "0 1px 2px rgba(14, 32, 24, 0.04)",
};

export const sectionTitleStyle = {
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: "0.01em",
  color: COLORS.ink,
  margin: 0,
};

export const sectionHintStyle = {
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 400,
  color: COLORS.inkMuted,
  margin: "2px 0 0",
};
